<template>
  <div class="i-earning-potential">
    <div class="header">
      <div>Your <strong>earnings potential.</strong></div>
      <div class="sub-header">People who test like you often make a defining career pivot within 3 months.</div>
    </div>
    <div class="main">
      <div class="graph">
        <div class="figure">+{{ salaryIncrease }}%</div>
      </div>
      <div
        v-if="!!personalityDescriptors.length"
        class="row-list"
      >
        <div
          v-for="descriptor in personalityDescriptors"
          :key="descriptor"
          class="detail"
        >
          {{ descriptor }}
        </div>
      </div>
    </div>
    <div class="action">
      <iBaseButton
        class="button"
        v-bind="buttonStyle"
        text="Find Out More"
        @clicked="$emit('scroll-to-payment')"
      />
      <div class="trustpilot">Excellent <img :src="IconStars" /><img :src="ImageStar" /> Trustpilot</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue"
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"
import { bluePurpleGradient, white } from "@/styles/colors"
import { breakpointSm } from "@/styles/variables"

import useRandomNumber from "@/composables/useRandomNumber"
import useIsScreenSize from "@/composables/useIsScreenSize"

import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import ImageStar from "@/assets/icons/icon-single-slashed-star.svg"
import IconStars from "@/assets/icons/icon-stars-four-point-five.svg"

// EMITS
defineEmits(["scroll-to-payment"])

// STORES AND COMPOSABLES
const reportStore = useReportStore()
const userStore = useUserStore()
const isSmOrLarger = useIsScreenSize(breakpointSm)

//COMPUTED
const buttonStyle = computed(() => {
  return {
    backgroundImage: bluePurpleGradient,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: isSmOrLarger.value ? "max-content" : "100%",
    padding: isSmOrLarger.value ? "8px 70px" : "10px 6px",
    size: "medium"
  }
})
const personalityDescriptors = computed(() => reportStore.reportData?.results?.personalityDescriptors)
const salaryIncrease = computed(() => {
  const potentialPercent = reportStore.salaryStatistics.comparisons.currentPotentialPercent
  const randomIncrease = useRandomNumber(userStore.getFullName || "Full Name", 20, 35)

  return potentialPercent < 0 ? -potentialPercent : randomIncrease
})
</script>

<style scoped lang="scss">
.i-earning-potential {
  @include flex(column, center, center, $gap: 35px);

  background: linear-gradient(180deg, #fff 0%, #eae7ff 100%);
  padding: 50px 15px;

  @media screen and (min-width: $breakpoint-md) {
    padding: 60px 15px;
    gap: 45px;
  }

  .header {
    @include display-md;
    @include flex(column, start, center, $gap: 20px);

    text-align: center;

    @media screen and (min-width: $breakpoint-md) {
      @include display-lg;
    }

    .sub-header {
      @include text-xl;

      font-weight: 700;
      text-align: center;
    }
  }

  .main {
    @include flex(column, center, center, $gap: 24px);

    .graph {
      @include border-radius-sm;

      position: relative;
      background-image: url("@/assets/welcome/salary-graph-mob.webp");
      background-color: $white;
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center;
      width: 100%;
      max-width: 343px;
      height: 688px;
      overflow: hidden;

      @media screen and (min-width: $breakpoint-md) {
        @include border-radius-md;

        background-color: transparent;
        background-position: center center;
        max-width: unset;
        width: 515px;
        height: 1032px;
      }

      @media screen and (min-width: $breakpoint-lg) {
        background-image: url("@/assets/welcome/salary-graph.webp");
        width: 790px;
        height: 470px;
      }

      .figure {
        @include display-lg;

        font-weight: 600;
        position: absolute;
        top: 70px;
        left: 25px;

        @media screen and (min-width: $breakpoint-md) {
          top: 100px;
          left: 40px;
        }

        @media screen and (min-width: $breakpoint-lg) {
          top: 70px;
          left: 32px;
        }
      }
    }

    .row-list {
      @include flex(row, center, center, wrap, $gap: 6px 20px);
      @include text-sm;

      font-weight: 400;

      .detail {
        @include flex(row, start, center);

        color: $black950;
        list-style: none;

        &::before {
          content: "";
          display: inline-block;
          height: 20px;
          width: 25px;
          background-image: url("@/assets/icons/icon-green-check.svg");
          background-repeat: no-repeat;
        }
      }
    }
  }

  .action {
    @include flex(column, center, center, $gap: 12px);
    @include text-sm;

    .trustpilot {
      @include flex(row, center, center, $gap: 4px);

      font-weight: 600;

      img {
        margin-left: 8px;
      }
    }
  }
}
</style>
